import { useTranslation } from "@sagot/i18n";
import { DonutChart, Heatmap, Icon, Skeleton, type ChartTone } from "@sagot/ui";
import { useFormatters } from "../shared/format";
import { HEATMAP_DAYS, HOURS_PER_DAY, heatmapValues, type AnalyticsFilters } from "./analytics-model";
import { ChartCard } from "./chart-card";
import { useChannelShares, useHeatmap } from "./use-analytics";

/** Mockup donut order: widget brand, messenger navy, mobile light brand, web app signal, other light navy. */
const CHANNEL_TONES: readonly ChartTone[] = ["brand", "ink", "brand-light", "signal", "ink-light", "brand-dark", "success"];
const NOON = 12;

function hourLabel(hour: number): string {
  if (hour === 0) return "12a";
  if (hour < NOON) return `${hour.toString()}a`;
  if (hour === NOON) return "12p";
  return `${(hour - NOON).toString()}p`;
}

export function ChannelSplitCard({ filters }: { filters: AnalyticsFilters }) {
  const { t } = useTranslation();
  const fmt = useFormatters();
  const query = useChannelShares(filters);
  return (
    <ChartCard
      testId="analytics-channels"
      title={t("engage.analytics.channels.title")}
      query={query}
      isEmpty={(data) => data.total === 0}
      empty={{ icon: "bar-chart", body: t("engage.analytics.channels.emptyBody") }}
      skeleton={
        <div className="flex flex-col items-center gap-5 sm:flex-row xl:flex-col 2xl:flex-row">
          <Skeleton className="size-40 rounded-pill" />
          <div className="w-full space-y-2">
            {Array.from({ length: 4 }, (_, i) => (
              <Skeleton key={i} className="h-4 w-full" />
            ))}
          </div>
        </div>
      }
    >
      {(data) => (
        <DonutChart
          className="xl:flex-col 2xl:flex-row"
          title={t("engage.analytics.channels.title")}
          centerValue={fmt.number(data.total)}
          centerLabel={t("engage.analytics.channels.center")}
          formatValue={fmt.number}
          data={data.items.map((item, i) => ({
            label: t(`engage.channels.${item.key}`, { defaultValue: item.key }),
            value: item.count,
            tone: CHANNEL_TONES[i % CHANNEL_TONES.length] ?? "brand",
          }))}
        />
      )}
    </ChartCard>
  );
}

export function BusiestHoursCard({ filters }: { filters: AnalyticsFilters }) {
  const { t } = useTranslation();
  const fmt = useFormatters();
  const query = useHeatmap(filters);
  const hours = Array.from({ length: HOURS_PER_DAY }, (_, h) => hourLabel(h));
  return (
    <ChartCard
      className="xl:col-span-2"
      testId="analytics-heatmap"
      title={t("engage.analytics.heatmap.title")}
      subtitle={t("engage.analytics.heatmap.subtitle", { timeZone: fmt.timeZone })}
      query={query}
      isEmpty={(data) => data.total === 0}
      empty={{ icon: "clock" }}
      skeleton={<Skeleton className="h-48 w-full" />}
    >
      {(data) => (
        <>
          {/* `relative overflow-hidden`: contains the sr-only 25-column fallback table, which otherwise widens the page at 390px. */}
          <div className="relative overflow-hidden">
            <Heatmap
              title={t("engage.analytics.heatmap.title")}
              rows={HEATMAP_DAYS.map((d) => t(`engage.analytics.heatmap.days.${d}`))}
              columns={hours}
              values={heatmapValues(data.cells)}
              formatValue={fmt.number}
              legendLabels={{ less: t("engage.analytics.heatmap.less"), more: t("engage.analytics.heatmap.more") }}
            />
          </div>
          {data.after_hours_pct === null ? null : (
            <p className="mt-3 flex items-center gap-1.5 text-xs text-fg-muted">
              <Icon name="info" className="size-3.5 text-fg-subtle" />
              {t("engage.analytics.heatmap.afterHours", { pct: fmt.percent(data.after_hours_pct) })}
            </p>
          )}
        </>
      )}
    </ChartCard>
  );
}
